import type React from 'react';

import { Button, Text } from '@change/ds-core-ui';
import { LockOutlineIcon } from '@change/ds-icons';

import type { I18nUtilities } from '@change-corgi/core-react/i18n';

import type { FacebookGroupRecommendation } from '#src/app/pages/psf/shared/api';

import { ShieldIcon } from './components/ShieldIcon';
import type { Step } from './hooks';

type StepCtaParams = {
	step: Step;
	translate: I18nUtilities['translate'];
	onContinue: () => void;
	onPost: () => void;
	selectedGroup: FacebookGroupRecommendation | null;
	onPostToGroup: ((group: FacebookGroupRecommendation) => void) | undefined;
};

/**
 * EXPERIMENT: psf_promote_or_share_facebook_post_sheet_experiment.
 * The step's primary Button. A plain function, not a component: the footer maps its children to find
 * and size its Buttons, and it would not find one inside a wrapper component.
 */
export function renderStepCta({ step, translate, onContinue, onPost, selectedGroup, onPostToGroup }: StepCtaParams) {
	if (step === 'destination') {
		return (
			<Button
				key="continue-cta"
				variant="primary"
				className="w-full"
				onClick={onContinue}
				data-testid="facebook-post-sheet-continue-cta"
			>
				{translate('corgi.psf.promote_or_share.facebook_post_sheet.continue_cta')}
			</Button>
		);
	}
	if (step === 'groups') {
		return (
			<Button
				key="group-cta"
				variant="primary"
				className="w-full"
				disabled={!selectedGroup}
				onClick={() => {
					if (selectedGroup) onPostToGroup?.(selectedGroup);
				}}
				data-testid="facebook-post-sheet-group-cta"
			>
				{translate('corgi.psf.promote_or_share.facebook_post_sheet.group_cta')}
			</Button>
		);
	}
	return (
		<Button
			key="post-cta"
			variant="primary"
			className="w-full"
			onClick={onPost}
			data-testid="facebook-post-sheet-post-cta"
		>
			{translate('corgi.psf.promote_or_share.facebook_post_sheet.post_cta')}
		</Button>
	);
}

/**
 * The footer's children. The note sits above the CTA and the domain line below it, as the prototype
 * draws them, so they are children here rather than the `disclaimer` prop, which always renders under
 * the buttons. An array, not a fragment: the footer maps its children to find and size its Buttons,
 * and React's children helpers do not look inside a fragment.
 */
export function renderFooter(cta: React.ReactElement, translate: I18nUtilities['translate']) {
	return [
		<Text
			key="review-note"
			as="p"
			size="x-small"
			className="text-typography-weak flex w-full items-center justify-center gap-1.5"
		>
			<ShieldIcon />
			{translate('corgi.psf.promote_or_share.facebook_post_sheet.review_note')}
		</Text>,
		cta,
		<Text
			key="domain"
			as="p"
			size="x-small"
			className="text-typography-weak flex w-full items-center justify-center gap-1"
		>
			<LockOutlineIcon size="x-small" />
			{translate('corgi.psf.promote_or_share.facebook_post_sheet.link_card_domain')}
		</Text>,
	];
}
